(ns practitest.frontend.init
  (:require
    [ajax.core :as ajax]
    [practitest.frontend.common.dom :as dom]
    [re-frame.core :as re-frame]
    [reagent.dom :as rdom]
    [goog.object :as gobj]
    ["@emotion/react" :as EmotionReact]
    ["@emotion/cache" :default createCache]
    ;; To initialise global interceptor
    [practitest.frontend.interceptors :as interceptors]
    ;; For keyboard shortcuts handler to be initialized
    [practitest.frontend.common.keyboard]
    [practitest.frontend.common.i18n :as i18n]
    ;; To init the TS integrations
    [practitest.frontend.ts-utils]))

;; 1. Tries to restore uuid from session storage, otherwise generates new one
;; 2. Sets HTTP header to be sent on AJAX calls
;; 3. Sets cookie with that uuid to pass it in non-AJAX calls
(defn init-ui-uuid! []
  (let [uuid (if-let [session-uuid (dom/get-session-item "pt_uuid_value")]
               session-uuid
               (let [new-uuid (random-uuid)]
                 ;; TODO: return from set-session-item ?
                 (dom/set-session-item "pt_uuid_value" new-uuid)
                 new-uuid))

        ui-uuid-interceptor (ajax/to-interceptor
                              {:name    "UI UUID Interceptor"
                               :request #(assoc-in % [:headers "X-PT_UI_UUID"] (str uuid))})]

    ;; No need to protect this UUID, it's not security-related
    (set! (.-cookie js/document) (str "PT_UI_UUID=" uuid))

    (swap! ajax/default-interceptors (partial cons ui-uuid-interceptor))

    uuid))

(defn init-csrf! []
  (when-let [csrf-token (some-> js/document
                                (.querySelector "meta[name='csrf-token']")
                                (.getAttribute "content"))]
    (let [csrf-interceptor (ajax/to-interceptor
                             {:name    "CSRF Interceptor"
                              :request #(assoc-in % [:headers "X-CSRF-Token"] csrf-token)})]
      (swap! ajax/default-interceptors (partial cons csrf-interceptor)))))

;; Bound components cache for hot reload to work
(defonce bound-components (atom {}))
;;
;; Structure:
;; {<customId> {:root <HTMLNode>
;;              :id <customId>
;;              :component-type "testset-view"
;;              :styles-cache <StylesCache>}}
;;

(defn- mount-root [root styles form props after-render-callback]
  (rdom/render
    ;; Cache for CSS-in-JS emotions library
    ;; https://github.com/JedWatson/react-select/issues/3680#issuecomment-924248517
    [:> EmotionReact/CacheProvider {:value styles}
     [form props]]
    root
    (or after-render-callback
        ;; Dummy callback
        (fn []))))

(defn attributes->map
  [node]
  (when node
    (into {}
          (map (fn [item]
                 [(keyword (.-nodeName item))
                  (.-nodeValue item)]))
          (seq (.-attributes node)))))

(defn remount-roots [component-type root-view]
  ;; remount all components of that type registered in cache
  (doseq [{:keys [root
                  styles-cache
                  webcomponent]}
          (filter #(= (:component-type %)
                      component-type)
                  (vals @bound-components))]
    (mount-root root styles-cache root-view (attributes->map webcomponent) nil)))

(defn init-ui! [{:keys [root-view webcomponent bare? after-render-callback]}]
  (when (and root-view webcomponent)
    (let [app-root   (if bare?
                       (.-shadowRoot webcomponent)
                       ;; Grab first div inside <body> element by default
                       (-> webcomponent
                           (.-shadowRoot)
                           (.querySelector "body > div")))
          element-id (or (.-customId webcomponent)
                         :global)]
      (let [styles (createCache #js {:container app-root
                                     :key       "shadowcache"})]
        ;; Store component in atom for reloads
        (swap! bound-components assoc element-id
               {:root           app-root
                :component-type (.-localName webcomponent)
                :id             element-id
                :webcomponent   webcomponent
                :styles-cache   styles})

        ;; First mount of root
        (mount-root app-root styles root-view (attributes->map webcomponent) after-render-callback)))))

(defn init! [{:keys [options init-event-fn] :as params}]
  (let [{:keys [config]} (js->clj options :keywordize-keys true)
        ui-uuid (init-ui-uuid!)]

    (init-csrf!)
    (dom/set-favicon!)

    (when-let [translation (gobj/get options "translation")]
      (i18n/append-translation! (:current-locale config) (js->clj translation)))

    (let [init-events (init-event-fn (assoc config :ui-uuid ui-uuid))]
      (doseq [event (if (keyword? (first init-events)) [init-events] init-events)]
        (re-frame/dispatch-sync event)))

    (init-ui! params)))
